import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from "@angular/forms";
import { DatePipe } from "@angular/common";
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { BaseControlComponent } from "../base-control/base-control.component";
import { ContextInteractionService } from "../../services/context-interaction.service";
import { IUploadFile, DocumentManagementService } from "../../../common/services/document-management.service";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { LoaderService } from "../../../common/services/loader.service";
import { ParseFileResponse, ParserError } from "../../../builder/components/state/controls/config/data/file-parser-config/parser.model";
import { UploadViewFilesComponent } from "../../../common/components/fileUpload/upload-view-files.component";


@Component({
    selector: 'app-bulk-well-transfer-parser',
    templateUrl: './bulk-well-transfer-parser.component.html',
    styleUrls: ['./bulk-well-transfer-parser.component.scss', '../file-upload/file-upload.component.scss'],
    standalone: false
})
export class BulkWellTransferParserComponent extends BaseControlComponent implements OnInit, OnDestroy {
    static componentName = "BulkWellTransferParserComponent";

    @ViewChild("downloadLink") downloadLink: ElementRef;
    @ViewChild(UploadViewFilesComponent) filesComponent: UploadViewFilesComponent;

    get applicationNumber(): string {
        if (this.flowComponent == null || this.flowComponent.currentFlowIndex == null) return null;
        let applicationNumber = Object.prototype.hasOwnProperty.call(this.flowComponent.currentFlowIndex, 'applicationNumber')
            ? (this.flowComponent.currentFlowIndex as any).applicationNumber
            : this.flowComponent.currentFlowIndex.flowIdentifier;
        return applicationNumber;
    }

    get disable(): boolean {
        return this.isEdit || !this.isEnabled || !this.targetGridIsReady;
    }

    get disabledMessage(): string {
        let message: string = "File Upload is disabled";
        if (this.enableEditing == false) message = "File Upload is disabled for upload during review.";
        else if (this.targetGridIsReady === false) message = "File Upload is disabled awaiting destination data to load.";
        return message;
    }

    get errorGridColumns(): ColDef[] {
        return [
            {
                headerName: "Line",
                headerTooltip: "Line",
                field: "line",
                tooltipField: "line",
                suppressMovable: true,
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerName: "Column",
                headerTooltip: "Column",
                field: "column",
                tooltipField: "column",
                suppressMovable: true,
                minWidth: 125,
                maxWidth: 150
            } as ColDef,
            {
                headerName: "Error",
                headerTooltip: "Error",
                field: "error",
                tooltipField: "error",
                suppressMovable: true
            } as ColDef
        ];
    }

    get errorData(): ParserError[] {
        let value = this.getContext("errors");
        if (value == null || !Array.isArray(value)) {
            value = [];
            this.storeContext(value, "errors");
        }
        return value;
    }

    set errorData(value: ParserError[]) {
        this.storeContext(value, "errors");
    }

    errorGridOptions: GridOptions;
    errorGridApi?: GridApi;
    fileRead: boolean = false;

    get excelStyles(): any[] {
        if (this.formatter) return this.formatter.excelStyles;
        return [];
    }

    get hasFileListing(): boolean {
        return this.uploadedFiles != null && this.uploadedFiles.length > 0;
    }

	get hasHeaders(): boolean {
		var value = <boolean>this.getContext("headers");
		if (value == null) value = false;
		return value; 
	}

	set hasHeaders(value: boolean) {
		this.storeContext(value, "headers");
	}

	get headerKey(): string {
		return `${this.controlId}_header`;
	}


    parsedGridOptions: GridOptions;
    parsedGridApi?: GridApi;
    get parsedData(): any {
        return this.getContextValue();
    }

    set parsedData(value: any) {
        this.setContextValue(value);
    }

    get storeAPIs(): string {
        if (!this.componentData) this.componentData = { config: {} };
        if (!this.componentData.config) this.componentData.config = { storeAPIs: "" };
        if (!this.componentData.config.storeAPIs) this.componentData.config.storeAPIs = "";
        return this.componentData.config.storeAPIs;
    }

    set storeAPIs(value: string) {
        if (!this.componentData) this.componentData = { config: {} };
        if (!this.componentData.config) this.componentData.config = { storeAPIs: value };
        if (!this.componentData.config.storeAPIs) this.componentData.config.storeAPIs = value;
        this.componentData.config.storeAPIs = value;
    }

    get targetGridIsReady(): boolean {
        if (this.storeAPIs == null || this.storeAPIs.length === 0) return false;
        return <boolean>(this.getContextData(`${this.storeAPIs}.loaded`));
    }

    get uploadedFiles(): IUploadFile[] {
        let value = this.getContext("files");
        if (value == null || !Array.isArray(value)) {
            value = [];
            this.storeContext(value, "files");
        }
        return value;
    }

    set uploadedFiles(value: IUploadFile[]) {
        this.storeContext(value, "files");
    }

    deleteFileWarning: Dialog = new Dialog("Deleting any file will cause all selected APIs to become unselected.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.confirmDelete(); }), new DialogButton("No", false, () => { return; })])
    noFile: Dialog = new Dialog("File failed to upload.  Please try again.", [new DialogButton("OK", true, () => { return; })]);
    noStorage: Dialog = new Dialog("No configured storage location for parsing results.  Cancelling parsing.", [new DialogButton("OK", true, () => { return; })]);
    subscription: Subscription = new Subscription();

    constructor(svc: ContextInteractionService, private dialogService: DynamicDialogService, private docService: DocumentManagementService, private formatter: GridFormatterService, private loader: LoaderService, private datePipe: DatePipe) {
        super(svc);
    }

    ngOnInit() {
		super.ngOnInit();
		this.formGroup.addControl("hasHeaders", new FormControl(this.hasHeaders));
        this.setupGrids();
        if (this.uploadedFiles != null && this.uploadedFiles.length > 0) {
            this.fileRead = true;
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.subscription.unsubscribe();
    }

    confirmDelete(): void {
        this.uploadedFiles.forEach(f => this.filesComponent.removeFile(f));
        this.uploadedFiles = [];
        this.errorData = [];
        this.contextService.setContextData(`${this.storeAPIs}.value`, [], this.componentContext);        
        this.fileRead = false;
    }

    deleteFile(file: IUploadFile): void {
        this.deleteFileWarning.toggleDialog(true);
        this.dialogService.updateDialog(this.deleteFileWarning);
    }

    export(): void {
        const now = this.datePipe.transform(new Date(), "yyyy-MM-dd").toString();
        let fileName = `${now}`;
        if (this.parsedData.length > 0) {
             fileName = `Parsed_APIs_${this.applicationNumber}_${now}`;
            this.formatter.exportDataToExcel(fileName, 'ParsedAPIs', this.parsedGridApi, false);
        }
        if (this.errorData.length > 0) {
            fileName = `Parser_Errors_${this.applicationNumber}_${now}`;
            this.formatter.exportDataToExcel(fileName, "ParserErrors", this.errorGridApi, false);
        }
        
    }

    parseFile(file: IUploadFile): void {
        if (this.storeAPIs == null || this.storeAPIs.length == 0) {
            this.noStorage.toggleDialog(true);
            this.dialogService.updateDialog(this.noStorage);
        }
        else if (file == null) {
            this.noFile.toggleDialog(true);
            this.dialogService.updateDialog(this.noFile);
        }
        else if (this.flowComponent.workflow.organizationId != null) {
            this.uploadedFiles.push(file);
            this.loader.toggleLoader(true);
			let subs$ = this.docService.validateAPIs(this.flowComponent.workflow.organizationId, file, this.hasHeaders).pipe(
                tap((data: ParseFileResponse) => {
                    this.fileRead = true;
                    this.loader.toggleLoader(false);
                })
            ).subscribe(
                {
                    next:  (data: ParseFileResponse) => {
                        if (data != null) {
                          this.contextService.setContextData(`${this.storeAPIs}.value`, data.data, this.componentContext);
                          this.errorData = data.errors;
                          this.parsedData = data.data;
                        }
                },
                    error: (error) => {
                        console.error(error);
                        this.loader.toggleLoader(false);
                        this.dialogService.showError();
                    }
                }
            );
            this.subscription.add(subs$);
        }
	}

	setHasHeaders(evt: any): void {		
		this.hasHeaders = evt;
	}

    setupGrids(): void {
        this.errorGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.errorGridColumns, false, true, true, true);
        this.errorGridOptions.onGridReady = (params) => { 
            this.errorGridApi = params.api; 
            this.errorGridApi.sizeColumnsToFit();
        };
        let apiColumn: ColDef = {
            headerName: "API #",
            headerTooltip: "API #",
            field: "id",
            tooltipField: "id",
            suppressMovable: true,
            minWidth: 50,
            cellStyle: { 'color': 'green' },
            cellClass: "stringType"
        } as ColDef;
        this.parsedGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns([apiColumn], false, true, true, false);
        this.parsedGridOptions.onGridReady = (params) => { 
            this.parsedGridApi = params.api; 
            this.parsedGridApi.sizeColumnsToFit();
        };
    }
}
